import { useState, useEffect, useRef } from 'react';
import { complianceAPI, assetsAPI } from '../../api';
import toast from 'react-hot-toast';
import styles from './Compliance.module.css';

const STATUS_MAP = {
  valid:          { bg:'#dcfce7', color:'#15803d', label:'Valid' },
  expiring_soon:  { bg:'#fef9c3', color:'#a16207', label:'Expiring Soon' },
  expired:        { bg:'#fee2e2', color:'#b91c1c', label:'Expired' },
};

const STATUS_FILTERS = [
  { key:'',             label:'All Certificates' },
  { key:'valid',        label:'Valid' },
  { key:'expiring_soon',label:'Expiring Soon' },
  { key:'expired',      label:'Expired' },
];

function AddCertModal({ assets, onClose, onSave }) {
  const [form, setForm] = useState({ name:'', asset_id:'', issuing_body:'', certificate_no:'', issue_date:'', expiry_date:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = async () => {
    if (!form.name || !form.expiry_date) { toast.error('Name and expiry date required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => { if (v) fd.append(k, v); });
      await complianceAPI.create(fd);
      toast.success('Certificate added'); onSave();
    } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className={styles.modal_overlay} onClick={e => e.target === e.currentTarget}>
      <div className={styles.modal} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_header}><h2>Add Certificate</h2><button onClick={onClose} className={styles.modal_close}>✕</button></div>
        <div className={styles.modal_body}>
          <div className={styles.form_grid}>
            <div className={`${styles.form_group} ${styles.form_full}`}><label>Certificate Name *</label><input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Fire Safety Certificate"/></div>
            <div className={`${styles.form_group} ${styles.form_full}`}><label>Related Asset</label><select value={form.asset_id} onChange={e=>set('asset_id',e.target.value)}><option value="">General (not asset-specific)</option>{assets.map(a=><option key={a.id} value={a.id}>{a.asset_code} — {a.name}</option>)}</select></div>
            <div className={styles.form_group}><label>Issuing Body</label><input value={form.issuing_body} onChange={e=>set('issuing_body',e.target.value)} placeholder="e.g. Fire Department"/></div>
            <div className={styles.form_group}><label>Certificate No.</label><input value={form.certificate_no} onChange={e=>set('certificate_no',e.target.value)} placeholder="e.g. CERT-2025-001"/></div>
            <div className={styles.form_group}><label>Issue Date</label><input type="date" value={form.issue_date} onChange={e=>set('issue_date',e.target.value)}/></div>
            <div className={styles.form_group}><label>Expiry Date *</label><input type="date" value={form.expiry_date} onChange={e=>set('expiry_date',e.target.value)}/></div>
            <div className={`${styles.form_group} ${styles.form_full}`}><label>Notes</label><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows={2} placeholder="Optional notes…"/></div>
          </div>
        </div>
        <div className={styles.modal_footer}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_submit} onClick={submit} disabled={saving}>{saving?'Saving…':'Add Certificate'}</button>
        </div>
      </div>
    </div>
  );
}

function DetailPanel({ cert, onDelete }) {
  if (!cert) return (
    <div className={styles.detail_empty}>
      <div style={{fontSize:36}}>🛡️</div>
      <div>Select a certificate to view details</div>
    </div>
  );
  const st = STATUS_MAP[cert.status] || STATUS_MAP.valid;
  const daysLeft = cert.expiry_date ? Math.round((new Date(cert.expiry_date) - Date.now()) / 86400000) : null;
  return (
    <div className={styles.detail_panel}>
      <div className={styles.dp_header}>
        <span className={styles.badge} style={{background:st.bg,color:st.color}}>{st.label}</span>
        {daysLeft !== null && daysLeft < 90 && (
          <span className={styles.badge} style={{background: daysLeft<0?'#fee2e2':daysLeft<30?'#fef9c3':'#fff7ed', color: daysLeft<0?'#b91c1c':daysLeft<30?'#a16207':'#c2410c'}}>
            {daysLeft < 0 ? `${Math.abs(daysLeft)}d expired` : `${daysLeft}d left`}
          </span>
        )}
      </div>
      <div className={styles.dp_title}>{cert.name}</div>
      {cert.notes && <p className={styles.dp_desc}>{cert.notes}</p>}
      <div className={styles.dp_actions}>
        <button className={styles.dp_btn_danger} onClick={onDelete}>🗑 Delete</button>
      </div>
      <div className={styles.dp_divider}/>
      <div className={styles.dp_section}>Certificate Details</div>
      {[
        ['Asset', cert.asset_name || 'General'],
        ['Issuing Body', cert.issuing_body],
        ['Certificate No.', cert.certificate_no],
        ['Issue Date', cert.issue_date?.slice(0,10)],
        ['Expiry Date', cert.expiry_date?.slice(0,10)],
        ['Added On', cert.created_at?.slice(0,10)],
      ].filter(([,v])=>v).map(([l,v])=>(
        <div key={l} className={styles.dp_row}><span>{l}</span><strong>{v}</strong></div>
      ))}
    </div>
  );
}

export default function CompliancePage() {
  const [certs,    setCerts]   = useState([]);
  const [assets,   setAssets]  = useState([]);
  const [loading,  setLoading] = useState(true);
  const [selected, setSelected]= useState(null);
  const [showAdd,  setShowAdd] = useState(false);
  const [search,   setSearch]  = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = () => { setLoading(true); complianceAPI.list().then(r=>setCerts(r.data.data)).finally(()=>setLoading(false)); };
  useEffect(()=>{ load(); assetsAPI.list({limit:200}).then(r=>setAssets(r.data.data)).catch(()=>{}); },[]);

  const del = async () => {
    if (!selected || !window.confirm('Delete this certificate?')) return;
    await complianceAPI.delete(selected.id);
    toast.success('Deleted'); setCerts(c=>c.filter(x=>x.id!==selected.id)); setSelected(null);
  };

  const filtered = certs.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.issuing_body||'').toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const counts = certs.reduce((acc,c)=>({...acc,[c.status]:(acc[c.status]||0)+1}),{});

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <button className={styles.new_btn} onClick={()=>setShowAdd(true)}>＋ Add Certificate</button>
        <div className={styles.search_wrap}>
          <span className={styles.search_icon}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span>
          <input className={styles.search_input} placeholder="Search certificates…" value={search} onChange={e=>setSearch(e.target.value)}/>
          {search && <button className={styles.search_clear} onClick={()=>setSearch('')}>✕</button>}
        </div>
        <div className={styles.toolbar_right}><span className={styles.total_badge}>{certs.length} certificates</span></div>
      </div>

      <div className={styles.body}>
        <div className={styles.filter_col}>
          {STATUS_FILTERS.map(f => {
            const cnt = f.key ? (counts[f.key]||0) : certs.length;
            const col = STATUS_MAP[f.key]?.color || '#1a56db';
            return (
              <div key={f.key} className={`${styles.filter_item} ${statusFilter===f.key?styles.filter_active:''}`} onClick={()=>setStatusFilter(f.key)}>
                <span className={styles.filter_dot} style={{background:col}}/>
                <span className={styles.filter_label}>{f.label}</span>
                <span className={`${styles.filter_count} ${statusFilter===f.key?styles.filter_count_active:''}`}>{cnt}</span>
              </div>
            );
          })}
          {(search||statusFilter) && <button className={styles.reset_btn} onClick={()=>{setSearch('');setStatusFilter('');}}>✕ Reset Filters</button>}
        </div>

        <div className={styles.list_col}>
          {loading ? <div className={styles.loading}>Loading…</div>
          : filtered.length === 0 ? (
            <div className={styles.empty}><div className={styles.empty_icon}>🛡️</div><div className={styles.empty_title}>No certificates found</div><div className={styles.empty_sub}>Add your first compliance certificate</div></div>
          ) : filtered.map(cert => {
            const st = STATUS_MAP[cert.status] || STATUS_MAP.valid;
            const daysLeft = cert.expiry_date ? Math.round((new Date(cert.expiry_date) - Date.now()) / 86400000) : null;
            const isSel = selected?.id === cert.id;
            return (
              <div key={cert.id} className={`${styles.card} ${isSel?styles.card_active:''}`} onClick={()=>setSelected(cert)}>
                <div className={styles.card_stripe} style={{background:st.color}}/>
                <div className={styles.card_body}>
                  <div className={styles.card_top}>
                    <span className={styles.card_ref}>{cert.certificate_no || '—'}</span>
                    <span className={styles.badge} style={{background:st.bg,color:st.color}}>{st.label}</span>
                  </div>
                  <div className={styles.card_title}>{cert.name}</div>
                  <div className={styles.card_meta}>{[cert.issuing_body, cert.asset_name||'General'].filter(Boolean).join(' · ')}</div>
                  <div className={styles.card_footer}>
                    <span style={{fontSize:12,color:'#64748b'}}>Expiry: {cert.expiry_date?.slice(0,10)||'—'}</span>
                    {daysLeft !== null && daysLeft < 90 && (
                      <span style={{fontSize:12,fontWeight:600,color:daysLeft<0?'#ef4444':daysLeft<30?'#f59e0b':'#f97316'}}>
                        {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d remaining`}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.card_actions}>
                  <button className={styles.card_action_btn} title="Delete" onClick={e=>{e.stopPropagation();setSelected(cert);del();}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.detail_col}>
          <DetailPanel cert={selected} onDelete={del}/>
        </div>
      </div>

      {showAdd && <AddCertModal assets={assets} onClose={()=>setShowAdd(false)} onSave={()=>{setShowAdd(false);load();}}/>}
    </div>
  );
}
